import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { StudyDocument } from './studyApi';
import { analysisInProgress, analysisPath, type StudyAnalysis } from './studyAnalysisApi';
import {
  ANALYSIS_STARS_FOR_UNREAD,
  analysisErrorMessage,
  analysisFailureMessage,
} from './studyAnalysisErrors';
import { StudyAnalyseConfirm } from './StudyAnalyseConfirm';
import { StudyCaptureFlow } from './StudyCaptureFlow';
import { materialAnalysable } from './studyPracticeTab';
import { useStudyAnalysis } from './useStudyAnalysis';

/**
 * Page analysis inside the material preview (study-capture-analyse-prd.md §2 "Just save it", §5):
 * a saved material can be analysed later, and an analysed one links to its result page.
 */

function AnalysisSummary({ analysis }: { analysis: StudyAnalysis }) {
  const failedPages = analysis.pages.filter((page) => page.status === 'failed');
  const questions = analysis.result?.questions.length ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="min-w-0 flex-1 text-sm font-bold text-ink" role="status">
        {analysisInProgress(analysis) && (
          <p>
            Analysing… {analysis.progress.pages_read} of {analysis.progress.pages_total} pages read
          </p>
        )}
        {analysis.status === 'succeeded' && (
          <p>
            {questions} {questions === 1 ? 'question' : 'questions'} found
            {analysis.result?.topic.label ? ` · ${analysis.result.topic.label}` : ''}
          </p>
        )}
        {analysis.status === 'partial' &&
          failedPages.map((page) => (
            <p key={page.page_index}>Page {page.page_index + 1} couldn&apos;t be read.</p>
          ))}
        {analysis.status === 'partial' && analysis.stars.refunded > 0 && (
          <p>{ANALYSIS_STARS_FOR_UNREAD}</p>
        )}
        {analysis.status === 'failed' && <p>{analysisFailureMessage(analysis)}</p>}
      </div>
      <Link
        to={analysisPath(analysis.primary_document_id, analysis.id)}
        className="btn-pill-secondary inline-flex items-center"
      >
        Open analysis
        <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  );
}

export function StudyMaterialAnalysisStrip({ document }: { document: StudyDocument }) {
  const navigate = useNavigate();
  const localDemo = isLocalStudyExperience();
  const [flowOpen, setFlowOpen] = useState(false);
  const analysis = useStudyAnalysis({
    documentId: !localDemo && materialAnalysable(document) ? document.id : null,
  });
  if (!materialAnalysable(document)) return null;
  if (localDemo)
    return (
      <section
        aria-label="Page analysis"
        className="rounded-2xl bg-wash-sky p-3 text-xs font-semibold text-slate2"
      >
        This demo includes pre-generated Tutor explanations. Automatic page analysis is not run.
      </section>
    );
  const openResult = (created: StudyAnalysis) =>
    navigate(analysisPath(created.primary_document_id, created.id));

  let body;
  if (analysis.isLoading) {
    body = <p className="text-sm font-bold text-slate2">Checking for an analysis…</p>;
  } else if (analysis.data) {
    body = <AnalysisSummary analysis={analysis.data} />;
  } else if (analysis.isError) {
    body = (
      <p className="text-sm font-bold text-ink-soft" role="status">
        {analysisErrorMessage(
          analysis.error,
          'Analysis could not be checked. Close and open this material again.',
        )}
      </p>
    );
  } else if (document.mime_type === 'application/pdf') {
    body = (
      <button type="button" className="btn-pill-primary" onClick={() => setFlowOpen(true)}>
        <Sparkles className="mr-2 inline h-4 w-4" aria-hidden="true" />
        Choose pages to analyse
      </button>
    );
  } else {
    body = (
      <StudyAnalyseConfirm
        compact
        pages={[{ document_id: document.id, page_number: 1 }]}
        onAnalysed={openResult}
        onPrepare={() => setFlowOpen(true)}
      />
    );
  }

  return (
    <section
      aria-label="Page analysis"
      className="border-b border-hairline bg-wash-sky px-5 py-3 sm:px-7"
    >
      {body}
      {flowOpen && (
        <StudyCaptureFlow
          source={{ kind: 'document', document }}
          onClose={() => setFlowOpen(false)}
          onSaved={() => setFlowOpen(false)}
          onAnalysed={openResult}
        />
      )}
    </section>
  );
}
